/* A Leading Role - make saved favorites follow the customer, not the browser.
 *
 * alr.js keeps the wishlist in localStorage under alr_wishlist_<customerId>,
 * so a list saved on a phone is invisible on a laptop. Its functions live
 * inside an IIFE and cannot be called from here, so this does not try to wrap
 * them. It works on the same localStorage key alr.js already uses:
 *
 *   on load  - read the customer metafield, union it with whatever is local,
 *              write the result back to localStorage and repaint the hearts
 *   on click - after alr.js has updated localStorage, push the new list up
 *
 * Entirely fail-safe. If the Worker is unreachable, every fetch is swallowed
 * and the existing local-only behaviour continues exactly as before.
 */
(function () {
  var GET = '/apps/concierge/wishlist/get';
  var SET = '/apps/concierge/wishlist/set';
  var PUSH_DELAY = 900;

  var customerId = null;
  var storageKey = null;
  var pushTimer = null;
  var lastPushed = null;

  function customer() {
    try {
      var node = document.getElementById('c-customer-data');
      var data = JSON.parse(node.textContent);
      if (data && data.logged_in && data.id) return String(data.id);
    } catch (err) {}
    return null;
  }

  function readLocal() {
    try {
      var raw = localStorage.getItem(storageKey);
      var arr = raw ? JSON.parse(raw) : [];
      return Array.isArray(arr) ? arr : [];
    } catch (err) {
      return [];
    }
  }

  function writeLocal(list) {
    try {
      localStorage.setItem(storageKey, JSON.stringify(list));
    } catch (err) {}
  }

  function union(a, b) {
    var out = [];
    var seen = {};
    [a, b].forEach(function (src) {
      (src || []).forEach(function (h) {
        if (typeof h !== 'string') return;
        var k = h.trim().toLowerCase();
        if (!k || seen[k]) return;
        seen[k] = 1;
        out.push(k);
      });
    });
    return out;
  }

  var same = function (a, b) {
    return a.length === b.length && a.slice().sort().join('|') === b.slice().sort().join('|');
  };

  // Repaint the hearts and the header count. alr.js has already run its own
  // pass by the time the server list arrives, so this redoes that work using
  // the same DOM contract rather than calling into it.
  function paint(list) {
    var inList = {};
    list.forEach(function (h) { inList[h] = 1; });

    document.querySelectorAll('.wlbtn[data-wl]').forEach(function (btn) {
      var on = !!inList[String(btn.getAttribute('data-wl')).toLowerCase()];
      btn.classList.toggle('on', on);
      btn.setAttribute('aria-pressed', on ? 'true' : 'false');
    });

    var count = document.getElementById('wl-count');
    if (count) {
      count.textContent = list.length;
      count.classList.toggle('show', list.length > 0);
    }
  }

  function push(list) {
    if (lastPushed && same(list, lastPushed)) return;
    lastPushed = list.slice();
    fetch(SET, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ handles: list })
    }).catch(function () {
      lastPushed = null; // let the next change retry
    });
  }

  function schedulePush() {
    if (pushTimer) clearTimeout(pushTimer);
    pushTimer = setTimeout(function () {
      pushTimer = null;
      push(readLocal());
    }, PUSH_DELAY);
  }

  function start() {
    customerId = customer();
    if (!customerId) return; // guests keep the old local-only behaviour
    storageKey = 'alr_wishlist_' + customerId;

    fetch(GET, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: '{}'
    })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (data) {
        if (!data || !Array.isArray(data.handles)) return;
        var local = readLocal();
        var merged = union(local, data.handles);
        lastPushed = data.handles.slice();

        if (!same(merged, local)) {
          writeLocal(merged);
          paint(merged);
        }
        // Anything saved while signed out on this device is now theirs.
        if (!same(merged, data.handles)) push(merged);
      })
      .catch(function () {});

    // alr.js writes localStorage inside its own click handler; run after it.
    document.addEventListener('click', function (e) {
      if (e.target && e.target.closest && e.target.closest('.wlbtn[data-wl]')) {
        setTimeout(schedulePush, 0);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
